import React, { useState } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Avatar, 
  Typography, 
  Chip, 
  List, 
  ListItem, 
  ListItemAvatar, 
  ListItemText,
  Tabs, 
  Tab, 
  Badge,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  Skeleton,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Campaign as CampaignIcon,
  Handshake as PartnershipIcon,
  Payments as PayoutIcon,
  Schedule as PendingIcon,
  PlayArrow as OngoingIcon,
  CheckCircle as CompletedIcon,
  Refresh as RefreshIcon,
  Launch as LaunchIcon,
  TrendingUp as TrendingIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import API from '../../../API';
import { handleAvatarError } from '../../../Utils';
import profilePhoto from '../../../Components/globalAssets/ppfLogo.png';

// Activity type configurations
const ACTIVITY_CONFIGS = {
  Campaign: {
    icon: CampaignIcon,
    color: 'primary',
  },
  Partnership: {
    icon: PartnershipIcon,
    color: 'success', 
  },
  Payout: {
    icon: PayoutIcon,
    color: 'info',
  }
};

// Category configurations
const CATEGORY_CONFIGS = {
  all: {
    icon: TrendingIcon,
    color: 'primary',
    label: 'All Activity'
  },
  pending: {
    icon: PendingIcon,
    color: 'warning',
    label: 'Pending'
  },
  ongoing: {
    icon: OngoingIcon,
    color: 'success',
    label: 'Ongoing'
  },
  previous: {
    icon: CompletedIcon,
    color: 'default',
    label: 'Previous'
  },
};

// Generate activity details
function getActivityDetails(activity) {
  const basePrice = activity.net_amount || 0;
  const formattedPrice = `$${basePrice.toLocaleString(undefined, { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;

  switch (activity.activity_type) {
    case 'Campaign':
      const campaignStatus = activity.creator_status === 'Pitched' ? 'received an offer for a' : 
                            activity.creator_status === 'Completed' ? 'completed' : 'is working on';
      return {
        primary: `${activity.creator_id} ${campaignStatus} campaign`,
        secondary: `"${activity.activity_name}"`,
        tertiary: `${activity.campaign_status} • ${formattedPrice}`,
        link: `/agency/creators/${activity.creator_id}/collaborations/campaigns/${activity.activity_id}`,
        editLink: `/creator/${activity.creator_id}/edit`,
        statusChip: activity.creator_status,
        date: activity.activity_date,
        creatorId: activity.creator_id,
      };

    case 'Partnership':
      const partnershipStatus = activity.creator_status === 'Pending' ? 'has a pending' :
                               activity.creator_status === 'Active' ? 'is in active' : 'completed';
      return {
        primary: `${activity.creator_id} ${partnershipStatus} partnership`,
        secondary: `"${activity.activity_name}"`,
        tertiary: `${activity.creator_status} • ${formattedPrice}`,
        link: `/agency/creators/${activity.creator_id}/collaborations/partnerships/${activity.activity_id}`,
        editLink: `/creator/${activity.creator_id}/edit`,
        statusChip: activity.creator_status,
        date: activity.activity_date,
        creatorId: activity.creator_id,
      };

    case 'Payout':
      return {
        primary: `${activity.creator_id} received a payout`,
        secondary: `PO: ${activity.activity_name}`,
        tertiary: `Paid • ${formattedPrice}`,
        link: `/agency/creators/${activity.creator_id}/payouts`,
        editLink: `/creator/${activity.creator_id}/edit`,
        statusChip: 'Completed',
        date: activity.activity_date,
        creatorId: activity.creator_id,
      };

    default:
      return {
        primary: 'Unknown activity',
        secondary: 'N/A',
        tertiary: formattedPrice,
        link: '',
        editLink: '',
        statusChip: 'Unknown',
        date: activity.activity_date,
        creatorId: activity.creator_id,
      };
  }
}

// Get status chip color
function getStatusChipColor(status, activityType) {
  if (activityType === 'Payout') return 'success';
  
  switch (status?.toLowerCase()) {
    case 'pitched':
    case 'pending':
      return 'warning';
    case 'active':
    case 'launched':
      return 'info';
    case 'completed':
      return 'success';
    default:
      return 'default';
  }
}

// Loading skeleton
function ActivitySkeleton() {
  return (
    <ListItem sx={{ py: 2 }}>
      <ListItemAvatar>
        <Skeleton variant="circular" width={48} height={48} />
      </ListItemAvatar>
      <ListItemText
        primary={<Skeleton variant="text" width="60%" />}
        secondary={
          <Stack spacing={1}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="30%" />
          </Stack>
        }
      />
    </ListItem>
  );
}

// Activity item with actions
function ActivityItem({ activity, onCreatorSelect }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const details = getActivityDetails(activity);
  const ActivityIcon = ACTIVITY_CONFIGS[activity.activity_type]?.icon || TrendingIcon;
  const activityConfig = ACTIVITY_CONFIGS[activity.activity_type];

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleGoToItem = () => {
    handleMenuClose();
    navigate(details.link);
  };

  const handleViewDealFlow = () => {
    handleMenuClose();
    if (onCreatorSelect) {
      onCreatorSelect(details.creatorId);
    }
  };

  const handleEditCreator = () => {
    handleMenuClose();
    navigate(details.editLink);
  };

  return (
    <>
      <ListItem
        sx={{
          py: 2,
          borderRadius: 2,
          mb: 1,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(activityConfig?.bgColor || theme.palette.action.hover, 0.5),
          }
        }}
        onClick={handleGoToItem}
      >
        <ListItemAvatar>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              <Avatar
                sx={{
                  width: 20,
                  height: 20,
                  bgcolor: `${activityConfig?.color}.main`,
                  color: 'white'
                }}
              >
                <ActivityIcon sx={{ fontSize: 12 }} />
              </Avatar>
            }
          >
            <Avatar 
              src={activity.profile_picture || profilePhoto}
              onError={handleAvatarError(profilePhoto)}
              alt={activity.creator_id}
              sx={{ width: 48, height: 48 }}
            >
              {activity.creator_id?.[0]?.toUpperCase()}
            </Avatar>
          </Badge>
        </ListItemAvatar>
        
        <ListItemText
          primary={
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {details.primary}
            </Typography>
          }
          secondary={
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary" noWrap>
                {details.secondary}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={details.statusChip}
                  size="small"
                  color={getStatusChipColor(details.statusChip, activity.activity_type)}
                  variant="outlined"
                />
                <Typography variant="caption" color="text.secondary">
                  {new Date(details.date).toLocaleDateString()}
                </Typography>
              </Stack>
            </Stack>
          }
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" color="primary" fontWeight="bold">
            {details.tertiary.split(' • ')[1] || details.tertiary}
          </Typography>
          <IconButton 
            size="small" 
            onClick={handleMenuOpen}
            sx={{ ml: 1 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </ListItem>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleGoToItem}>
          <ListItemIcon>
            <LaunchIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Go to Item</Typography>
        </MenuItem>
        <MenuItem onClick={handleViewDealFlow}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">View in Deal Flow</Typography>
        </MenuItem>
        <MenuItem onClick={handleEditCreator}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Edit Creator</Typography>
        </MenuItem>
      </Menu>
    </>
  );
}

// Empty state
function EmptyState({ category }) {
  const config = CATEGORY_CONFIGS[category];
  const Icon = config.icon;

  return (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <Icon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        No {config.label.toLowerCase()} activities
      </Typography>
      <Typography variant="body2" color="text.disabled">
        {category === 'pending' && "No new activities require your attention."}
        {category === 'ongoing' && "No activities are currently in progress."}
        {category === 'previous' && "No completed activities to show."}
        {category === 'all' && "No activities found."}
      </Typography>
    </Box>
  );
}

export default function AgencyZapfeed({ onCreatorSelect }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState('all');

  // Fetch activity data
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agency', 'activity-feed', { filter: activeTab }],
    queryFn: () => API.agency.getZapfeed({ filter: activeTab }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const activities = data?.activities || [];
  const categoryCounts = data?.category_counts || {};

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleRefresh = () => {
    refetch();
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          action={
            <IconButton size="small" onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          }
        >
          Failed to load activity feed. Click to retry.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Tabs */}
      <Box sx={{ px: 3, pt: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
        >
          {Object.entries(CATEGORY_CONFIGS).map(([key, config]) => (
            <Tab
              key={key}
              value={key}
              label={
                <Badge 
                  badgeContent={categoryCounts[key] || 0} 
                  color={config.color}
                  max={99}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <config.icon fontSize="small" />
                    <Typography variant="body2" fontWeight={500}>
                      {config.label}
                    </Typography>
                  </Stack>
                </Badge>
              }
              sx={{ textTransform: 'none', minHeight: 48 }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2 }}>
        {isLoading ? (
          <List>
            {Array.from({ length: 6 }).map((_, index) => (
              <ActivitySkeleton key={index} />
            ))}
          </List>
        ) : activities.length === 0 ? (
          <EmptyState category={activeTab} />
        ) : (
          <List sx={{ py: 0 }}>
            {activities.map((activity, index) => (
              <React.Fragment key={`${activity.activity_type}-${activity.activity_id}-${activity.creator_id}`}>
                <ActivityItem 
                  activity={activity} 
                  onCreatorSelect={onCreatorSelect}
                />
                {index < activities.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Footer */}
      {!isLoading && activities.length > 0 && (
        <>
          <Divider />
          <Box sx={{ p: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Showing {activities.length} {activeTab !== 'all' ? activeTab : ''} activities
              </Typography>
              <Stack direction="row" spacing={2}>
                {Object.entries(categoryCounts)
                  .filter(([key]) => key !== 'all')
                  .map(([key, count]) => (
                    <Stack key={key} direction="row" alignItems="center" spacing={0.5}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: `${CATEGORY_CONFIGS[key].color}.main`
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {count} {key}
                      </Typography>
                    </Stack>
                  ))}
              </Stack>
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
}